import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';
import { Seo } from '@/components/common';
import { useProducer } from '@/hooks/useProducer';
import { usePublicProduct } from '@/hooks/usePublicProduct';
import PickupOrderForm from '@/components/farmshop/PickupOrderForm';
import { producerPublicPath, producerShopPath } from '@/lib/paths';

export default function ProducerProductOrderPage() {
  const { slugOrId, productSlug } = useParams<{ slugOrId: string; productSlug: string }>();
  const { producer } = useProducer(slugOrId || '');
  const { product } = usePublicProduct(producer?.id, productSlug);

  const producerPathKey = producer?.slug || slugOrId || '';
  const producerUrl = producerPublicPath(producerPathKey);
  const shopUrl = producerShopPath(producerPathKey);

  if (!product || !producer) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <SiteHeader />
        <main className="flex-1 container py-16 text-center">
          <h1 className="font-display text-2xl text-foreground">Beställning</h1>
          <p className="text-muted-foreground mt-4">Produkten kunde inte hämtas.</p>
          <Link to={shopUrl} className="underline mt-6 block">Tillbaka till gårdsbutiken</Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const productForDialog = {
    id: product.id,
    title: product.title,
    price: product.price_ore / 100,
    currencyCode: product.currency || 'SEK',
    imageUrl: undefined,
    pickupInstructions: product.pickup_instructions ?? null,
    stockLimit: (product as any).stock_limit ?? null,
    remaining: null as number | null,
  };

  const seoTitle = `${product.title} – Beställning`;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Seo title={seoTitle} />
      <SiteHeader />
      <main className="flex-1 container py-8 max-w-3xl" data-testid="producer-product-order-page">
        <h1 className="font-display text-2xl mb-4">Fortsätt beställning</h1>
        <p className="text-sm text-muted-foreground mb-6">Förbeställ för upphämtning på gården. Ingen betalning sker nu.</p>

        <PickupOrderForm
          initialProduct={productForDialog}
          producerId={producer.id}
          producerName={producer.name}
          pickupInstructions={producer.pickup_instructions}
          sourceSurface="product_page_dedicated"
        />
      </main>
      <SiteFooter />
    </div>
  );
}
